import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface Node {
  id: string;
  x: number;
  y: number;
  layer: number;
}
interface Connection {
  from: Node;
  to: Node;
  id: string;
}
interface Pulse {
  id: number;
  connectionId: string;
  progress: number;
}
export function NeuralNetwork() {
  const [pulses, setPulses] = useState<Pulse[]>([]);
  const [pulseCounter, setPulseCounter] = useState(0);
  const layers = useMemo(() => [5, 7, 5, 4], []);
  const svgWidth = 500;
  const svgHeight = 500;
  const nodes = useMemo<Node[]>(() => {
    const result: Node[] = [];
    const layerSpacing = svgWidth / (layers.length + 1);
    layers.forEach((count, layerIdx) => {
      const nodeSpacing = svgHeight / (count + 1);
      for (let i = 0; i < count; i++) {
        result.push({
          id: `${layerIdx}-${i}`,
          x: layerSpacing * (layerIdx + 1),
          y: nodeSpacing * (i + 1),
          layer: layerIdx
        });
      }
    });
    return result;
  }, [layers]);
  const connections = useMemo<Connection[]>(() => {
    const result: Connection[] = [];
    for (let l = 0; l < layers.length - 1; l++) {
      const fromNodes = nodes.filter(n => n.layer === l);
      const toNodes = nodes.filter(n => n.layer === l + 1);
      fromNodes.forEach(from => {
        toNodes.forEach(to => {
          result.push({
            from,
            to,
            id: `${from.id}-${to.id}`
          });
        });
      });
    }
    return result;
  }, [nodes, layers]);
  useEffect(() => {
    const interval = setInterval(() => {
      const randomConn = connections[Math.floor(Math.random() * connections.length)];
      setPulseCounter(prev => prev + 1);
      setPulses(prev => {
        const newPulses = [...prev, {
          id: pulseCounter,
          connectionId: randomConn.id,
          progress: 0
        }];
        return newPulses.slice(-8);
      });
    }, 400);
    return () => clearInterval(interval);
  }, [connections, pulseCounter]);
  useEffect(() => {
    const animFrame = setInterval(() => {
      setPulses(prev => prev.map(p => ({
        ...p,
        progress: p.progress + 0.04
      })).filter(p => p.progress <= 1));
    }, 16);
    return () => clearInterval(animFrame);
  }, []);
  return <motion.div className="w-full h-full flex items-center justify-center" animate={{
    y: [0, -8, 0]
  }} transition={{
    duration: 6,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-51">
      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full h-full max-w-[500px] max-h-[500px]" aria-hidden="true" data-id="element-52">
        <defs data-id="element-53">
          <filter id="nodeGlow" data-id="element-54">
            <feGaussianBlur stdDeviation="4" result="blur" data-id="element-55" />
            <feMerge data-id="element-56">
              <feMergeNode in="blur" data-id="element-57" />
              <feMergeNode in="SourceGraphic" data-id="element-58" />
            </feMerge>
          </filter>
          <filter id="pulseGlow" data-id="element-59">
            <feGaussianBlur stdDeviation="6" result="blur" data-id="element-60" />
            <feMerge data-id="element-61">
              <feMergeNode in="blur" data-id="element-62" />
              <feMergeNode in="SourceGraphic" data-id="element-63" />
            </feMerge>
          </filter>
          <radialGradient id="nodeGrad" data-id="element-64">
            <stop offset="0%" stopColor="#A78BFA" data-id="element-65" />
            <stop offset="100%" stopColor="#8B5CF6" data-id="element-66" />
          </radialGradient>
        </defs>

        {/* Connections */}
        {connections.map(conn => <line key={conn.id} x1={conn.from.x} y1={conn.from.y} x2={conn.to.x} y2={conn.to.y} stroke="#8B5CF6" strokeOpacity={0.1} strokeWidth={0.8} data-id="element-67" />)}

        {/* Pulses traveling along connections */}
        <AnimatePresence data-id="element-68">
          {pulses.map(pulse => {
          const conn = connections.find(c => c.id === pulse.connectionId);
          if (!conn) return null;
          const px = conn.from.x + (conn.to.x - conn.from.x) * pulse.progress;
          const py = conn.from.y + (conn.to.y - conn.from.y) * pulse.progress;
          return <motion.circle key={pulse.id} cx={px} cy={py} r={4} fill="#D4AF37" filter="url(#pulseGlow)" initial={{
            opacity: 0,
            r: 2
          }} animate={{
            opacity: [0, 1, 1, 0],
            r: [2, 5, 5, 2]
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} data-id="element-69" />;
        })}
        </AnimatePresence>

        {/* Active connection highlights */}
        {pulses.map(pulse => {
        const conn = connections.find(c => c.id === pulse.connectionId);
        if (!conn) return null;
        return <line key={`line-${pulse.id}`} x1={conn.from.x} y1={conn.from.y} x2={conn.to.x} y2={conn.to.y} stroke="#D4AF37" strokeOpacity={0.25 * (1 - pulse.progress)} strokeWidth={1.5} data-id="element-70" />;
      })}

        {/* Nodes */}
        {nodes.map(node => <g key={node.id} data-id="element-71">
            <circle cx={node.x} cy={node.y} r={6} fill="url(#nodeGrad)" filter="url(#nodeGlow)" opacity={0.9} data-id="element-72" />
            <circle cx={node.x} cy={node.y} r={3} fill="#C4B5FD" opacity={0.6} data-id="element-73" />
          </g>)}
      </svg>
    </motion.div>;
}